import { FurnitureJson } from '../Data';
import { Cabinet } from './Cabinet';
import cloneDeep from 'lodash/cloneDeep';
import { getFurnitureName, getFurnitureNames } from '../utils/nameGenerator';
import {
  getFurnitureDescription,
  getFurnitureDescriptions,
} from '../utils/descriptionGenerator';
import { formatNumber } from '../utils/helpers';
import { CustomPiece } from '../pieces/CustomPiece';
import { UpperShelf } from '../pieces/UpperShelf';
import { Color } from './Color';
import { getPiecesData } from '../utils/dataStorage';
import { Position } from '../constants';

interface CabinetEntry {
  order: number;
  cabinet: Cabinet;
}

export class Furniture {
  public cabinets: CabinetEntry[];
  public name?: string;
  public image?: string;
  private _id?: string;
  private _reference?: string;
  private _prestashopId?: string;
  public upperShelf?: CustomPiece<UpperShelf>;

  constructor(
    cabinets?: CabinetEntry[],
    name?: string,
    image?: string,
    reference?: string,
    upperShelf?: CustomPiece<UpperShelf>,
  ) {
    this.name = name;
    if (cabinets) this.cabinets = cabinets;
    else this.cabinets = [];
    this.reference = reference;
    this.image = image;

    if (upperShelf) this.upperShelf = upperShelf;
  }

  static fromModel(model: FurnitureJson): Furniture {
    const { name } = model;

    const cabinets: CabinetEntry[] = [];
    for (const cabinetEntry of model.cabinets) {
      const { order } = cabinetEntry;
      const cabinet = Cabinet.fromModel(cabinetEntry.cabinet);

      cabinets.push({ order: order, cabinet: cabinet });
    }
    cabinets.sort((a, b) => b.order - a.order);

    return new Furniture(cabinets, name, model.image, `f${model.id}`);
  }

  static replacer(key: string, value: any): any {
    // Filtering out properties for serialization
    const filterOut = ['id'];
    if (filterOut.includes(key)) {
      return undefined;
    }
    if (key[0] === '_') {
      return undefined;
    }
    return value;
  }

  toJSON() {
    return Object.assign({}, this);
  }

  get id(): string | undefined {
    return this._id;
  }

  set id(value: string | undefined) {
    this._id = value;
  }

  get prestashopId(): string | undefined {
    return this._prestashopId;
  }

  set prestashopId(value: string | undefined) {
    this._prestashopId = value;
  }

  get reference(): string | undefined {
    return this._reference;
  }

  set reference(value: string | undefined) {
    this._reference = value;
  }

  get(id: string): Cabinet | null {
    const entry = this.cabinets.find(e => e.cabinet.id === id);
    if (entry) return entry.cabinet;
    return null;
  }

  getByIndex(index: number): Cabinet | undefined {
    if (index >= 0 && index < this.cabinets.length) {
      return this.cabinets[index].cabinet;
    }
    return undefined;
  }

  getByOrder(order: number): Cabinet {
    /**
     * Return the cabinet at the order if exist
     */
    const result = this.cabinets.filter(e => e.order === order);
    if (!result || result.length === 0)
      throw new Error(`There is no cabinet at order ${order}`);
    if (result.length > 1) {
      throw new Error('There should not have multiple cabinets at same order');
    }
    return result[0].cabinet;
  }

  get height(): number {
    /**
     * Get max height
     */
    let height = 0;
    for (const entry of this.cabinets) {
      if (height < entry.cabinet.height) {
        height = entry.cabinet.height;
      }
    }
    if (this.upperShelf) {
      height += this.upperShelf.height;
    }
    return height;
  }

  get heightWithGroundOffset(): number {
    /**
     * Get max height with ground offset
     * Used by 3D viewer to set camera Z axis
     */
    let height = 0;
    for (const entry of this.cabinets) {
      if (height < entry.cabinet.height + entry.cabinet.groundOffset) {
        height = entry.cabinet.height + entry.cabinet.groundOffset;
      }
    }
    return height;
  }

  get weight(): number {
    /**
     * Get weight of the whole furniture
     */
    let weight = 0;
    for (const entry of this.cabinets) {
      weight += entry.cabinet.weight;
    }
    if (this.upperShelf) {
      weight += this.upperShelf.weight;
    }
    return weight;
  }

  get depth(): number {
    /**
     * Get max depth
     */
    let depth = 0;
    for (const entry of this.cabinets) {
      if (depth < entry.cabinet.depth) {
        depth = entry.cabinet.depth;
      }
    }
    return depth;
  }

  get width(): number {
    /**
     * Get total width
     */
    let width = 0;
    for (const entry of this.cabinets) {
      width += entry.cabinet.width;
    }
    return width;
  }

  get quantity(): number {
    return this.cabinets.length;
  }

  get price(): number {
    let price = 0;
    for (const entry of this.cabinets) {
      price += entry.cabinet.price;
    }
    if (this.upperShelf) {
      price += this.upperShelf.price;
    }
    return price;
  }

  get salesPrice(): number {
    let price = 0;
    for (const entry of this.cabinets) {
      price += Math.round(entry.cabinet.price * 1.21);
    }
    return price / 1.21;
  }

  getSalesPriceTaxInclude(vat: number, resellerRate: number) {
    /**
     * Price Tax Exclude
     */
    return this.salesPrice * resellerRate * vat;
  }

  displayPriceTaxInclude(
    vat = 1.21,
    resellerRate = 1,
    virgule = '.',
    thousenSeparator = ',',
  ): string {
    /**
     * We don't round if we have fraction because resallers don't use VAT price
     */
    return formatNumber(
      this.getSalesPriceTaxInclude(vat, resellerRate),
      virgule,
      thousenSeparator,
    );
  }

  get sortedCabinets() {
    /**
     * Get the cabinets sorted by order
     */
    return this.cabinets.sort((a, b) => a.order - b.order);
  }

  add(cabinet: Cabinet, position?: number): Furniture {
    /**
     * Add a cabinet to the furniture
     * @position:  add at the position and adjust the order
     */
    if (position === undefined) {
      this.cabinets.push({ order: this.cabinets.length, cabinet: cabinet });
    } else {
      if (position <= this.cabinets.length) {
        for (const entryCabinet of this.cabinets) {
          if (entryCabinet.order >= position) {
            entryCabinet.order += 1;
          }
        }
        this.cabinets.push({ order: position, cabinet: cabinet });
      } else {
        throw new Error(
          `Position ${position} is not valid, there is only ${this.cabinets.length} in the furniture `,
        );
      }
    }
    return this;
  }

  remove(order: number) {
    let removed = undefined;
    this.cabinets = this.cabinets.filter(e => e.order === order);
    for (const entry of this.cabinets) {
      if (entry.order === order) removed = entry;
      else if (entry.order > order) {
        entry.order -= 1;
      }
    }
    return removed;
  }

  removeById(id: string): Cabinet | undefined {
    /**
     * Remove the cabinet with the id and adapt the order
     */
    let removed: undefined | CabinetEntry = undefined;
    const furniture = new Furniture();
    for (const cabinetEntry of this.cabinets) {
      if (cabinetEntry.cabinet.id === id) {
        removed = cabinetEntry;
      } else {
        if (removed) {
          if (removed.order < cabinetEntry.order) {
            furniture.add(cabinetEntry.cabinet, cabinetEntry.order - 1);
          } else {
            furniture.add(cabinetEntry.cabinet, cabinetEntry.order);
          }
        } else {
          furniture.add(cabinetEntry.cabinet, cabinetEntry.order);
        }
      }
    }
    this.cabinets = furniture.cabinets;
    return removed?.cabinet;
  }

  get first(): Cabinet {
    return this.getByOrder(0);
  }

  get last(): Cabinet | undefined {
    if (this.cabinets.length > 0) {
      return this.getByOrder(this.cabinets.length - 1);
    }
    return undefined;
  }

  clone(): Furniture {
    return cloneDeep(this);
  }

  reorder(source: number, destination: number): Furniture {
    /**
     * Switch the element order
     * Start become the destination
     */
    if (source === destination) return this;
    let sourceEntry: CabinetEntry | undefined = undefined;
    for (const entry of this.cabinets) {
      if (entry.order === source) {
        sourceEntry = entry;
      }

      if (source < destination) {
        if (entry.order > source && entry.order <= destination) {
          entry.order -= 1;
        }
      } else {
        if (entry.order >= destination && entry.order < source) {
          entry.order += 1;
        }
      }
    }
    if (sourceEntry) {
      sourceEntry.order = destination;
    } else {
      throw new Error('Source or destination are not valid');
    }
    return this;
  }

  replace(cabinet: Cabinet, id?: string): Furniture {
    /**
     * Replace the cabinet in furniture by a new one
     * @id: the id which to replace, if none, replace the same cabinet id that already exist in the furniture
     */
    if (!id) {
      if (!this.get(cabinet.id)) {
        throw new Error(
          "We can't replace cabinet without id or if the id don't already exist",
        );
      }
      id = cabinet.id;
    }
    for (const entry of this.cabinets) {
      if (entry.cabinet.id === id) {
        entry.cabinet = cabinet;
      }
    }
    return this;
  }

  getName(language: string): string {
    return getFurnitureName(this, language);
  }

  getDescription(language: string): string {
    return getFurnitureDescription(this, language);
  }

  get names(): { [id: string]: string } {
    return getFurnitureNames(this);
  }

  get descriptions(): { [id: string]: string } {
    return getFurnitureDescriptions(this);
  }

  addOrUpdateUpperShelf(width?: number, depth?: number, color?: Color) {
    if (!color) {
      if (!this.upperShelf) {
        throw new Error('Should have a color to update upperShelf');
      }
      color = this.upperShelf?.color;
    }
    const searchInfo = UpperShelf.getSearchInfos(
      width ?? this.width,
      depth ?? this.depth,
      color,
    );
    const customP = getPiecesData().upperShelves.getCustom(
      searchInfo,
      Position.Front,
    );
    if (customP) {
      this.upperShelf = customP;
    } else {
      throw new Error('Could not find the upper shelf');
    }
  }
}
